import type { JSXElementConstructor } from "react"

import {
  AmazonSellingPartnerForm,
  ArchiveForm,
  AttentiveForm,
  FacebookMarketingForm,
  FacebookPagesForm,
  GA4Form,
  GA4AirbyteForm,
  GoogleAdsManagementForm,
  GoogleKeywordSearchForm,
  InstagramBusinessForm,
  YotpoForm,
  TargetForm,
  SephoraForm,
  InstagramInsightsForm,
  SpsForm,
} from "../forms"

import type { CustomConnectorDialogFormProps } from "./types"

export const serviceConnectorMap: Record<
  string,
  {
    type: "connector" | "upload"
    component: JSXElementConstructor<CustomConnectorDialogFormProps>
    showForStatuses?: string[] // Optional field to specify which statuses should show the form
  }
> = {
  archive: { type: "connector", component: ArchiveForm },
  attentive: { type: "connector", component: AttentiveForm },
  ga4: {
    type: "connector",
    component: GA4Form,
    showForStatuses: [
      "PENDING_SETUP",
      "FAILED",
      "RECONNECT",
      "UPGRADE",
      "CONNECTING",
    ], // Show GA4 form for setup completion statuses
  },
  google_analytics_4_airbyte: {
    type: "connector",
    component: GA4AirbyteForm,
    showForStatuses: [
      "PENDING_SETUP",
      "FAILED",
      "RECONNECT",
      "UPGRADE",
      "CONNECTING",
    ], // Show GA4 Airbyte form for Airbyte connector setup completion statuses
  },
  // Agency/MCC model: no OAuth — the dialog collects the client's Google Ads
  // customer id after they accept our Manager-account link in Google Ads.
  google_ads_management: {
    type: "connector",
    component: GoogleAdsManagementForm,
  },
  google_keyword_search: {
    type: "connector",
    component: GoogleKeywordSearchForm,
  },
  yotpo: { type: "connector", component: YotpoForm },
  target: { type: "upload", component: TargetForm },
  sephora: { type: "upload", component: SephoraForm },
  // Note: walmart_yearly_report and other ingestion connectors are automatically
  // detected via isIngestionConnector() utility and use IngestionForm
  instagram_insights: { type: "connector", component: InstagramInsightsForm },
  sps: { type: "connector", component: SpsForm },
}

/**
 * Info step components shown before the connection flow begins.
 * These display prerequisite information and require user acknowledgment
 * before proceeding with the normal connector flow (including Airbyte OAuth).
 */
export const infoStepMap: Record<
  string,
  JSXElementConstructor<{
    isOpen: boolean
    onClose: () => void
    onContinue: () => void
  }>
> = {
  facebook_marketing_airbyte: FacebookMarketingForm,
  facebook_pages_airbyte: FacebookPagesForm,
  instagram_business_airbyte: InstagramBusinessForm,
}

/**
 * Forms shown before a Fivetran connector is created, for services whose Fivetran
 * `config` must already carry a value at `POST /connectors` time. They collect the
 * value and hand it back so it can be sent as `preConfig` on the create call.
 *
 * This is the collecting counterpart of infoStepMap (pre-connect, no values) and the
 * opposite of serviceConnectorMap (post-connect, too late to affect the create).
 */
export const preConnectFormMap: Record<
  string,
  JSXElementConstructor<{
    isOpen: boolean
    onClose: () => void
    onSubmit: (preConfigValues: Record<string, unknown>) => void
  }>
> = {
  amazon_selling_partner: AmazonSellingPartnerForm,
}

/**
 * Get list of services that should auto-open their setup forms after OAuth
 * Based on services that have custom forms with showForStatuses configuration
 */
export const getAutoOpenServices = (): string[] =>
  Object.entries(serviceConnectorMap)
    .filter(
      ([, config]) =>
        config.showForStatuses && config.showForStatuses.length > 0,
    )
    .map(([serviceName]) => serviceName)

/**
 * Check if a service should auto-open its setup form after OAuth
 */
export const shouldAutoOpenService = (serviceName: string): boolean =>
  getAutoOpenServices().includes(serviceName)
